import dynamic from "next/dynamic";

import { getBuilderModelName } from "@/libs/builder/tools/getBuilderModelName";
import { withValidation } from "@/libs/builder/validators/validateBuilderComponentData";

export const flavorFinderInputs = [
  // GENERAL
  {
    name: "generalTab",
    type: "object",
    friendlyName: "General",
    subFields: [
      {
        name: "totalNumberOfQuestions",
        friendlyName: "Total number of questions",
        type: "number",
        model: getBuilderModelName("flavor-finder-question"),
      },
      {
        name: "hideNextButton",
        type: "boolean",
        friendlyName: "Hide Next CTA button",
      },
      {
        name: "nextCtaLabel",
        type: "string",
        required: true,
        friendlyName: "Next CTA Label",
        showIf: (values) => values.get("hideNextButton") !== true,
      },
      {
        name: "backCtaLabel",
        type: "string",
        required: true,
        friendlyName: "Back CTA Label",
      },
      {
        name: "questionNumberIndicator",
        type: "string",
        required: true,
        friendlyName: "Question Number Indicator",
      },
      {
        name: "selectionIndicator",
        type: "string",
        required: true,
        friendlyName: "Selection Indicator",
      },
      {
        name: "skipCtaLabel",
        type: "string",
        required: true,
        friendlyName: "Skip CTA Label",
      },
      {
        name: "sharedFinalQuestion",
        type: "reference",
        friendlyName: "Shared Final Question",
        model: getBuilderModelName("flavor-finder-optional-question"),
      },
    ],
  },
  // INTRO SECTION
  {
    name: "introSectionTab",
    type: "object",
    friendlyName: "Intro Section",
    subFields: [
      {
        name: "indicatorOverrideLabel",
        type: "string",
        required: true,
        friendlyName: "Indicator Override Label",
      },
      {
        name: "titleLoggedIn",
        type: "string",
        required: true,
        friendlyName: "Title for logged in",
      },
      {
        name: "titleNonLoggedIn",
        type: "string",
        required: true,
        friendlyName: "Title for non logged in",
      },
      {
        name: "subtitle",
        type: "string",
        required: true,
        friendlyName: "Subtitle",
      },
      {
        name: "ctaLabelStartLoggedIn",
        type: "string",
        required: true,
        friendlyName: "CTA Label for start quiz as logged in user",
      },
      {
        name: "ctaLabelStartNonLoggedIn",
        type: "string",
        required: true,
        friendlyName: "CTA Label for start quiz as non logged in user",
      },
      {
        name: "ctaLabelLoginToStart",
        type: "string",
        required: true,
        friendlyName: "CTA Label for login to start the quiz",
      },
      {
        name: "introBackgroundImage",
        type: "file",
        required: true,
        friendlyName: "Intro background image (Desktop)",
      },
      {
        name: "mediumIntroBackgroundImage",
        type: "file",
        required: true,
        friendlyName: "Intro background image (Tablet)",
      },
      {
        name: "smallIntroBackgroundImage",
        type: "file",
        required: true,
        friendlyName: "Intro background image (Mobile)",
      },
      {
        name: "introBackgroundVideo",
        type: "file",
        required: true,
        friendlyName: "Intro background video (Desktop)",
      },
      {
        name: "mediumIntroBackgroundVideo",
        type: "file",
        friendlyName: "Intro background video (Tablet)",
      },
      {
        name: "smallIntroBackgroundVideo",
        type: "file",
        friendlyName: "Intro background video (Mobile)",
      },
    ],
  },
  // CONSENT POLICY
  {
    name: "consentPolicyTab",
    type: "object",
    friendlyName: "Consent Policy",
    subFields: [
      {
        name: "indicatorOverrideLabel",
        type: "string",
        required: true,
        friendlyName: "Indicator override label",
      },
      {
        name: "title",
        type: "string",
        required: true,
        friendlyName: "Title",
      },
      {
        name: "marketingMailConsentId",
        type: "string",
        required: true,
        friendlyName: "Consent purpose id for mail receiving label",
      },
      {
        name: "descriptionLoggedIn",
        type: "richText",
        required: true,
        friendlyName: "Description for logged in",
      },
      {
        name: "descriptionNotLoggedIn",
        type: "richText",
        required: true,
        friendlyName: "Description for not logged in",
      },
      {
        name: "skipAndShowResultsCtaLabel",
        type: "string",
        required: true,
        friendlyName: "Skip and Show Results CTA Label",
      },
      {
        name: "showResultsLabel",
        type: "string",
        required: true,
        friendlyName: "Show Results",
      },
      {
        name: "emailFieldLabel",
        type: "string",
        required: true,
        friendlyName: "Email Field label",
      },
      {
        name: "emailFieldPlaceholder",
        type: "string",
        required: true,
        friendlyName: "Email Field placeholder",
      },
      {
        name: "emailFieldValidationErrorMessage",
        type: "string",
        required: true,
        friendlyName: "Email Field validation error message",
      },
      {
        name: "sendResultsCTALabel",
        type: "string",
        required: true,
        friendlyName: "Send results CTA Label",
      },
      {
        name: "disclaimerLoggedInConsent",
        type: "richText",
        friendlyName: "Disclaimer for logged in and consent",
      },
      {
        name: "disclaimerLoggedInNoConsent",
        type: "richText",
        friendlyName: "Disclaimer for logged in and no consent",
      },
      {
        name: "disclaimerNotLoggedIn",
        type: "richText",
        required: true,
        friendlyName: "Disclaimer for not logged in",
      },
      {
        name: "backgroundImage",
        type: "file",
        required: true,
        friendlyName: "Background image",
      },
      {
        name: "mediumBackgroundImage",
        type: "file",
        friendlyName: "Tablet background image",
      },
      {
        name: "smallBackgroundImage",
        type: "file",
        friendlyName: "Mobile background image",
      },
      {
        name: "disableSendingMailResults",
        type: "boolean",
        required: true,
        defaultValue: false,
        friendlyName: "Disable sending mail results",
      },
    ],
  },
  // RESULTS SLIDE SECTION
  {
    name: "resultsSlideTab",
    type: "object",
    friendlyName: "Results Slide Section",
    subFields: [
      {
        name: "title",
        type: "string",
        required: true,
        friendlyName: "Title",
      },
      {
        name: "subtitle",
        type: "string",
        required: true,
        friendlyName: "Subtitle",
      },
      {
        name: "intensityLabel",
        type: "string",
        required: true,
        friendlyName: "Intensity label",
      },
      {
        name: "flavorLabel",
        type: "string",
        required: true,
        friendlyName: "Flavor label",
      },
      {
        name: "coolingLabel",
        type: "string",
        required: true,
        friendlyName: "Cooling label",
      },
      {
        name: "redoCtaLabel",
        type: "string",
        required: true,
        friendlyName: "Redo CTA Label",
      },
      {
        name: "finalActionCtaLabel",
        type: "string",
        required: true,
        friendlyName: "Final Action CTA Label",
      },
      {
        name: "finalActionCtaLink",
        type: "url",
        required: true,
        friendlyName: "Default results link (no device)",
      },
      {
        name: "resultsLinkWithDevice",
        type: "url",
        required: true,
        friendlyName: "Results link with device",
        defaultValue: "",
        showIf: (values) => values.get("deviceSuggestionCardEnabled") === true,
      },
      {
        name: "deviceSuggestionCardEnabled",
        type: "boolean",
        required: true,
        defaultValue: "false",
        friendlyName: "Enable device suggestion card",
      },
      {
        name: "deviceSuggestionDescription",
        type: "string",
        friendlyName: "Device Suggestion Description",
      },
      {
        name: "deviceSuggestionImgUrl",
        type: "file",
        friendlyName: "Device Suggestion Description",
      },
      {
        name: "deviceSuggestionState",
        type: "string",
        enum: ["none", "yes", "no"],
        friendlyName: "Default state of suggestion card",
      },
    ],
  },
] as const satisfies Inputs;

export const FlavorFinderComponent: RegisteredComponent = {
  name: "FlavorFinder",
  component: withValidation(
    flavorFinderInputs,
    dynamic(() =>
      import("./FlavorFinder").then(({ FlavorFinder }) => FlavorFinder)
    ),
    "FlavorFinder"
  ),
  inputs: flavorFinderInputs,
};
